'use client';

import {Auth0Provider, useAuth0} from '@auth0/auth0-react';
import React, {FC, PropsWithChildren, useEffect, useState} from 'react';
import {WebAuth} from 'auth0-js';
import {buildRedirectUrlTo, StepsPath} from '../routing/StepsRouting';
import {setUserProfile} from '../(state)/UserProfileState';
import {
  AuthentictaionState,
  GoogleUserProfile,
  isCiamEnabled,
} from '@gfiber/pixel-party';

const domain = process.env.NEXT_PUBLIC_AUTH0_DOMAIN;
const clientId = process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID;

export type RequestCodeStatus = 'inactive' | 'pending' | 'success' | 'error';

export interface CiamAuthContextProps {
  webAuth: null | WebAuth;
  codeRequestingStatus: RequestCodeStatus;
  setCodeRequestingStatus: (status: RequestCodeStatus) => void;
  codeSendingStatus: RequestCodeStatus;
  setCodeSendingStatus: (status: RequestCodeStatus) => void;
}

export const CiamAuthContext = React.createContext<CiamAuthContextProps>({
  webAuth: null,
  codeRequestingStatus: 'inactive',
  setCodeRequestingStatus: () => {},
  codeSendingStatus: 'inactive',
  setCodeSendingStatus: () => {},
});

/**
 *  We need use both, auth0 react library and js library,
 *  for social and email login. This provider combines them
 * */
export const CiamAuthProvider: FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren) => {
  const [webAuth, setWebAuth] = useState<null | WebAuth>(null);
  const [codeRequestingStatus, setCodeRequestingStatus] =
    useState<RequestCodeStatus>('inactive');
  const [codeSendingStatus, setCodeSendingStatus] =
    useState<RequestCodeStatus>('inactive');

  // Static page generation fix
  useEffect(() => {
    const auth = new WebAuth({
      clientID: clientId!,
      domain: domain!,
      responseType: 'code',
      scope: 'openid profile',
      redirectUri: buildRedirectUrlTo(StepsPath.SignInOTP),
    });

    setWebAuth(auth);
  }, [setWebAuth]);

  return (
    <Auth0Provider
      domain={domain!}
      clientId={clientId!}
      authorizationParams={{
        useRefreshTokens: true,
        scope: 'openid profile email offline_access',
      }}
    >
      <Auth0UserLinker>
        <CiamAuthContext.Provider
          value={{
            webAuth,
            codeRequestingStatus,
            setCodeRequestingStatus,
            codeSendingStatus,
            setCodeSendingStatus,
          }}
        >
          {children}
        </CiamAuthContext.Provider>
      </Auth0UserLinker>
    </Auth0Provider>
  );
};

export const useCiamAuthContext = (): CiamAuthContextProps =>
  React.useContext(CiamAuthContext);

export const Auth0UserLinker = ({children}: PropsWithChildren) => {
  const {user} = useAuth0();

  useEffect(() => {
    if (isCiamEnabled() && user) {
      const userProfile: GoogleUserProfile = {
        givenName: user.given_name!,
        familyName: user.family_name!,
        emailVerified: user.email_verified!.toString(),
        isExpired: false,
        name: user.name!,
        email: user.email!,
        locale: user.locale!,
      };
      setUserProfile(userProfile, AuthentictaionState.Authenticated);
    }
  }, [user]);

  return <>{children}</>;
};
